import {CM_LOGGER_APP} from "@app/common/services/logging"
import {HttpClient} from "@angular/common/http"
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    computed,
    inject,
    Injector,
    OnInit,
    viewChild,
    signal,
    DestroyRef,
    ChangeDetectionStrategy,
} from "@angular/core"
import {FormsModule} from "@angular/forms"
import {MatButtonModule} from "@angular/material/button"
import {MatIconModule} from "@angular/material/icon"
import {MatListModule} from "@angular/material/list"
import {MatTreeNestedDataSource} from "@angular/material/tree"
import {CdkTreeModule, NestedTreeControl} from "@angular/cdk/tree"
import {ActivatedRoute, Router} from "@angular/router"
import {MutationCreatePriceGraphInput, MutationUpdatePriceGraphInput, PriceGraphState} from "@generated"
import {Catalog, CatalogItem} from "@cm/pricing/catalogs/catalog-interface"
import {createSafariPriceGraph} from "@cm/pricing/catalogs/vado/safari-graph"
import {PricingNode} from "@cm/pricing/pricing-node"
import {ConditionalAmountGroupNode} from "@cm/pricing/nodes/conditional-amount-group-node"
import {ConditionalAmountNode} from "@cm/pricing/nodes/conditional-amount-node"
import {Currency} from "@cm/pricing/types/currency"
import {ConfiguratorVariant, PriceMap, PricedItem} from "@cm/pricing/types/pricing-types"
import {PricingContext} from "@cm/pricing/pricing-context"
import {PriceContainer} from "@cm/pricing/nodes/price-container"
import {PricedItemNode} from "@cm/pricing/nodes/priced-item-node"
import {checkConfigurationGroupNodes} from "@cm/pricing/verification"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {DialogSize} from "@common/models/dialogs"
import {RoutedDialogComponent} from "@common/components/dialogs/routed-dialog/routed-dialog.component"
import {
    PriceMappingCreatePriceGraphGQL,
    PriceMappingDeletePriceGraphGQL,
    PriceMappingGetPriceGraphsGQL,
    PriceMappingOrganizationIdFromTemplateIdGQL,
    PriceMappingUpdatePriceGraphGQL,
} from "@pricing/components/price-mapping/price-mapping.generated"
import {downloadFromMemory} from "@app/common/helpers/utils/download-from-memory"
import {ExporterYed} from "@app/platform/helpers/simple-graph/exporter/exporter-yed"
import {NodeSettingsComponent} from "@app/pricing/components/node-settings/node-settings/node-settings.component"
import {PriceTableComponent} from "@app/pricing/components/price-table/price-table.component"
import {PriceNodeVisualizerComponent} from "@pricing/components/node-visualizers/price-nodes-visualizer/price-node-visualizer.component"
import {getPriceMap} from "@pricing/helpers"
import {PriceGraphAccessor, PriceGraphBuilder} from "@pricing/models/pricing/price-graph"
import {ConfiguratorComponent} from "@app/common/components/viewers/configurator/configurator/configurator.component"
import {ConfigMenuComponent} from "@app/common/components/viewers/configurator/config-menu/config-menu.component"
import {getCatalogFromFileInput} from "@app/pricing/helpers/catalog"
import {importCatalog, updatePrices} from "@app/pricing/helpers/catalog-update"
import {ConfigMenuService} from "@app/common/components/viewers/configurator/config-menu/services/config-menu.service"
import {PriceGraphData} from "@cm/pricing/catalogs/graph-helpers"
import {PricingService} from "@pricing/services/pricing.service"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {parseXlsb, parseXlsb as parseXlsbVado, VadoCatalog} from "@cm/pricing/catalogs/vado/catalog" // eslint-disable-line
import {createCameoPriceGraph} from "@cm/pricing" // eslint-disable-line
import {createDiamanteGraph, createTesimaGraph, createUltimoGraph} from "@cm/pricing/catalogs/calypso/price-graphs" // eslint-disable-line

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-price-mapping",
    templateUrl: "./price-mapping.component.html",
    styleUrls: ["./price-mapping.component.scss"],
    imports: [
        RoutedDialogComponent,
        MatButtonModule,
        MatListModule,
        PriceTableComponent,
        CdkTreeModule,
        PriceNodeVisualizerComponent,
        FormsModule,
        ConfigMenuComponent,
        MatIconModule,
        NodeSettingsComponent,
        ConfiguratorComponent,
    ],
})
export class PriceMappingComponent implements OnInit, AfterViewInit {
    private readonly logger = inject(CM_LOGGER_APP)
    readonly $configuratorMenu = viewChild.required<ConfigMenuComponent>("configuratorMenu")
    readonly $configurator = viewChild.required<ConfiguratorComponent>("configurator")

    dialogSizes = DialogSize
    readonly $modified = signal(false)

    readonly $templateId = signal<string | undefined>(undefined)
    priceGraphId?: string
    readonly $priceGraphStates = signal(Object.values(PriceGraphState))
    priceGraphState: PriceGraphState = PriceGraphState.Draft

    priceGraphBuilder = new PriceGraphBuilder("test")

    catalog?: Catalog
    catalogNew?: Catalog
    readonly $priceMap = signal<PriceMap | undefined>(undefined)
    readonly $availableCurrencies = computed<Currency[]>(() => {
        const priceMap = this.$priceMap()
        return priceMap ? PricingContext.getAvailableCurrencies(priceMap) : []
    })
    readonly $pricingContext = computed<PricingContext | undefined>(() => {
        const priceMap = this.$priceMap()
        const currency = this.$selectedCurrency() ?? this.$availableCurrencies()[0]
        if (!priceMap || currency === undefined) return undefined
        return new PricingContext(priceMap, currency)
    })

    readonly $currentPrices = signal<PricedItem[]>([])
    readonly $priceIncludesVat = computed<boolean>(() => this.$pricingContext()?.getPriceIncludesVat() ?? true)
    readonly $selectedCurrency = signal<Currency | undefined>(undefined)
    error: string = ""

    //move this to own component
    readonly $searchResults = signal<CatalogItem[]>([])
    readonly $selectedSearchItem = signal<CatalogItem | undefined>(undefined)

    childrenAccessor = (node: PricingNode): PricingNode[] => {
        if (node instanceof PricedItemNode || node instanceof PriceContainer) return node.getSubprices()
        if (node instanceof ConditionalAmountGroupNode) return node.getAmountNodes()
        if (node instanceof ConditionalAmountNode) return []

        throw new Error("Unexpected node type")
    }

    readonly treeControl = new NestedTreeControl<PricingNode>(this.childrenAccessor)

    readonly $dataSource = signal(new MatTreeNestedDataSource<PricingNode>())
    readonly $selectedTreeNode = signal<PricingNode | undefined>(undefined)

    readonly organizationIdFromTemplateIdGql = inject(PriceMappingOrganizationIdFromTemplateIdGQL)
    readonly priceGraphsGql = inject(PriceMappingGetPriceGraphsGQL)
    readonly deletePriceGraphGql = inject(PriceMappingDeletePriceGraphGQL)
    readonly createPriceGraphGql = inject(PriceMappingCreatePriceGraphGQL)
    readonly updatePriceGraphGql = inject(PriceMappingUpdatePriceGraphGQL)

    readonly injector = inject(Injector)
    readonly pricingService = inject(PricingService)
    private readonly router = inject(Router)
    private readonly route = inject(ActivatedRoute)
    private readonly http = inject(HttpClient)
    private readonly configMenuService = inject(ConfigMenuService)
    private readonly destroyRef = inject(DestroyRef)
    private readonly changeDetectorRef = inject(ChangeDetectorRef)
    hasChild = (_: number, node: PricingNode) => {
        if (node instanceof PricedItemNode || node instanceof PriceContainer) return node.getSubprices().length > 0
        if (node instanceof ConditionalAmountGroupNode) return node.getAmountNodes().length > 0
        if (node instanceof ConditionalAmountNode) return false
        throw new Error("Unexpected node type")
    }

    ngOnInit() {
        this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => this.$templateId.set(params["templateuuid"]))
    }

    async ngAfterViewInit() {
        /*No proper catalog management yet, I first want to understand better what we actually get from the customers. So far, we have:
        - Darran: One consistent catalog, one inconsistent excel sheet with grades (subject to format changes)
        - Vado: Two loose excel sheets (subject to format changes)*/
        try {
            //Darran
            //this.catalog = await parseXml(await lastValueFrom(this.http.get("assets/DAR.xml", {responseType: "text"})))
            //this.catalogNew = await parseXml(await lastValueFrom(this.http.get("assets/DAR-new.xml", {responseType: "text"})))
            //Vado
            //const data = await lastValueFrom(this.http.get("assets/VADO Colormass Price Lists - October 2026 - Final 05.08.26.xlsx", {responseType: "blob"}))
            // const data = await lastValueFrom(this.http.get("assets/2026-08-10 - Cameo Price List.xlsx", {responseType: "blob"}))
            // this.catalog = parseXlsb(new Uint8Array(await data.arrayBuffer()), await import("xlsx"), this.logger) as VadoCatalog
            // this.$priceMap.set(this.catalog.getPriceMap())
            // this.initPriceGraph(createCameoPriceGraph(this.catalog as VadoCatalog))
            // Delvaux
            // const data = await lastValueFrom(this.http.get("assets/Colormass_FW25 MTO Prijs Lijst_June 2026.xlsx", {responseType: "blob"}))
            // this.catalog = parseXlsx(new Uint8Array(await data.arrayBuffer()), await import("xlsx")) as DelvauxCatalog
            // this.$priceMap.set(this.catalog.getPriceMap())
            // this.initPriceGraph(createMtoPriceGraph(this.catalog as DelvauxCatalog))
            // Calypso
            // const data = await lastValueFrom(this.http.get("assets/Calypso-Price-File-Spring-26-V3.xlsx", {responseType: "blob"}))
            // this.catalog = parseXlsx(new Uint8Array(await data.arrayBuffer()), await import("xlsx")) as CalypsoCatalog
            // this.$priceMap.set(this.catalog.getPriceMap())
            // this.initPriceGraph(createTesimaGraph(this.catalog as CalypsoCatalog))
            // this.initPriceGraph(createUltimoGraph(this.catalog as CalypsoCatalog))
            //this.initPriceGraph(createDiamanteGraph(this.catalog as CalypsoCatalog))
        } catch (error) {
            this.logger.error("Error reading catalog:", error)
        }

        if (this.$templateId()) {
            this.load()
        }
    }

    searchCatalog(searchTerm: string): void {
        if (this.catalog) {
            this.$searchResults.set(this.catalog.search(searchTerm))
        }
    }

    downloadGraph(): void {
        const simpleGraph = this.priceGraphBuilder.transformToSimpleGraph()
        const exporter = new ExporterYed()
        const xml = exporter.export(simpleGraph)

        downloadFromMemory(xml, "Pricegraph.graphml")
    }

    onSearchItemClick(catalogItem: CatalogItem): void {
        this.$selectedSearchItem.set(catalogItem)
    }

    onSearchItemDoubleClick(catalogItem: CatalogItem): void {
        this.$selectedSearchItem.set(catalogItem)

        const priceGraph = catalogItem.createPriceGraph((uniqueId: string) => {
            return this.priceGraphBuilder.getSubgraph(uniqueId)
        })

        this.priceGraphBuilder.addSubPriceGraph(priceGraph)
        this.$dataSource().data = this.priceGraphBuilder.getNodesForDisplay()
    }

    onTreeNodeClick(node: PricingNode, event: MouseEvent) {
        this.$selectedTreeNode.set(node)
        event.stopPropagation()
    }

    private canDrop(node: PricingNode): boolean {
        if (node instanceof PricedItemNode && node.parameters.condition) {
            return true
        }
        if (node instanceof ConditionalAmountNode) {
            return true
        }
        return node instanceof PriceContainer && !!node.parameters.condition
    }

    onDragOver(event: DragEvent, node: PricingNode): void {
        event.stopPropagation()
        if (!this.canDrop(node)) return
        event.preventDefault()
    }

    onDrop(event: DragEvent, node: PricingNode): void {
        event.preventDefault()
        event.stopPropagation()
        if (event.dataTransfer === null) return
        const serializedData = event.dataTransfer.getData("text/plain")
        const data = JSON.parse(serializedData) as ConfiguratorVariant

        if (!(node instanceof PricedItemNode) && !(node instanceof ConditionalAmountNode) && !(node instanceof PriceContainer))
            throw new Error("Unexpected node type")
        if (!node.canAddDependency(data.groupId, data.variantId)) throw new Error("Cannot add dependency") //the drag/drop api does not allow access to this data before the drop event

        this.priceGraphBuilder.addDependency(node, data).then(() => {
            this.updateCurrentConfigurations()
        })
    }

    load(): void {
        const templateId = this.$templateId()
        if (!templateId) throw new Error("No template uuid provided")
        this.$configurator().loadConfiguratorData({templateId: templateId}, undefined) //price graph loading is triggered by loadingCompleted of the scene viewer to prevent errors.
    }

    async updateCatalog() {
        const templateId = this.$templateId()
        if (!this.catalog || !this.catalogNew || !templateId) throw new Error("Catalogs or templateId not initialized")
        const organizationId = (await fetchThrowingErrors(this.organizationIdFromTemplateIdGql)({templateId})).template.organizationId
        updatePrices(organizationId, this.catalog, this.catalogNew, this.injector)
    }

    async importCatalog() {
        const templateId = this.$templateId()
        if (!this.catalog || !templateId) throw new Error("Catalogs or templateId not initialized")
        const organizationId = (await fetchThrowingErrors(this.organizationIdFromTemplateIdGql)({templateId})).template.organizationId
        importCatalog(organizationId, this.catalog, this.injector)
    }

    initSafariGraph() {
        this.initPriceGraph(createSafariPriceGraph(this.catalog as VadoCatalog))
    }

    async loadPriceGraph() {
        const {priceGraphs} = await fetchThrowingErrors(this.priceGraphsGql)({filter: {templateId: {equals: this.$templateId()}}})
        if (priceGraphs.length > 1) throw new Error("More than one price graph found, revisions not implemented yet")
        if (priceGraphs.length === 0) return

        const priceGraph = priceGraphs[0]!

        const graph = priceGraph.graph
        this.priceGraphBuilder.initFromSerialized(graph)
        this.priceGraphId = priceGraph.id

        if (!this.catalog) {
            const templateId = this.$templateId()
            if (!templateId) {
                throw new Error("No template uuid provided")
            }
            const organizationId = (await fetchThrowingErrors(this.organizationIdFromTemplateIdGql)({templateId})).template.organizationId
            this.$priceMap.set(await getPriceMap(this.injector, organizationId, this.priceGraphBuilder.rootNode.getOriginalIdsFromCatalog()))
        }
        this.$dataSource().data = []
        this.changeDetectorRef.detectChanges()
        this.$dataSource().data = this.priceGraphBuilder.getNodesForDisplay()
        this.priceGraphState = priceGraph.state
        this.logger.debug("price graph id", this.priceGraphId)

        this.updateCurrentConfigurations()
    }

    initPriceGraph(data: PriceGraphData): void {
        data.amountGroups.forEach((group) => this.priceGraphBuilder.addAmountGroupNode(group))
        data.priceContainers.forEach((container) => this.priceGraphBuilder.addSubPriceGraph(container))
        data.pricedItemNodes.forEach((pricedItem) => this.priceGraphBuilder.addSubPriceGraph(pricedItem))

        this.$dataSource().data = this.priceGraphBuilder.getNodesForDisplay()
    }

    async deletePriceGraph() {
        if (!this.priceGraphId) {
            throw new Error("No price graph uuid provided")
        }
        const result = await mutateThrowingErrors(this.deletePriceGraphGql)({priceGraphId: this.priceGraphId})
        this.logger.debug("Delete result", result)
    }

    async initCatalogFromLocalFile(event: Event) {
        this.catalog = await getCatalogFromFileInput(event, this.logger)
        this.$priceMap.set(this.catalog.getPriceMap())
    }

    async initNewCatalogFromLocalFile(event: Event) {
        this.catalogNew = await getCatalogFromFileInput(event, this.logger)
    }

    async verifyAndSave() {
        if (!this.priceGraphBuilder.rootNode) {
            return
        }

        const templateId = this.$templateId()
        if (!templateId) {
            throw new Error("No template uuid provided")
        }

        const organizationId = await fetchThrowingErrors(this.organizationIdFromTemplateIdGql)({templateId}).then(({template}) => template.organizationId)

        try {
            //Only save, if prices can be loaded for this graph, otherwise the price computation will not work in practice.
            await getPriceMap(this.injector, organizationId, this.priceGraphBuilder.rootNode.getOriginalIdsFromCatalog())
            checkConfigurationGroupNodes(this.priceGraphBuilder.rootNode)

            /*Check, to what extend this is actually necessary:
            - the check is *very* helpful for some graph types (Darran-style)
            - Cannot be used for Vado at the moment
            - The check must be adapted to new node conditions
            - Some nodes must be excludeable*/
            //checkMutuallyExclusiveConditions(this.priceGraphBuilder.rootNode)
            return this.save(organizationId)
        } catch (error) {
            this.logger.error("An error occurred, did not save:", error)
        }
    }

    async save(organizationId: string) {
        const originalIdsFromCatalog = this.priceGraphBuilder.rootNode.getOriginalIdsFromCatalog()
        if (!this.priceGraphId) {
            const templateId = this.$templateId()
            if (!templateId) {
                throw new Error("No template uuid provided")
            }
            const createPriceGraphInput: MutationCreatePriceGraphInput = {
                originalIdsFromCatalog: originalIdsFromCatalog,
                templateId,
                organizationId: organizationId,
                state: this.priceGraphState,
                graph: this.priceGraphBuilder.rootNode.serialize(),
            }
            const {createPriceGraph} = await mutateThrowingErrors(this.createPriceGraphGql)({input: createPriceGraphInput})
            this.priceGraphId = createPriceGraph?.id
        } else {
            const updatePriceGraphInput: MutationUpdatePriceGraphInput = {
                originalIdsFromCatalog: originalIdsFromCatalog,
                priceGraphId: this.priceGraphId,
                organizationId: organizationId,
                state: this.priceGraphState,
                graph: this.priceGraphBuilder.rootNode.serialize(), //graph: {key1: "value134"},
            }
            const result = await mutateThrowingErrors(this.updatePriceGraphGql)({input: updatePriceGraphInput})
            this.logger.debug("Update result", result)
        }
    }

    updateCurrentConfigurations(): void {
        this.updatePrice()
    }

    updatePrice(): void {
        const pricingContext = this.$pricingContext()
        if (!pricingContext) {
            this.logger.warn("Pricing context not initialized, cannot update prices")
            return
        }
        if (this.$selectedCurrency() === undefined) {
            this.$selectedCurrency.set(pricingContext.getCurrency())
        }
        const graphAccessor = new PriceGraphAccessor(this.priceGraphBuilder.rootNode)
        this.pricingService
            .getPricesForMappingData(graphAccessor, pricingContext)
            .then((result) => {
                this.$currentPrices.set(result)
                this.error = ""
            })
            .catch((error) => {
                this.error = error.message
                this.$currentPrices.set([])
            })
    }

    onCurrencyChange(currency: Currency): void {
        this.$selectedCurrency.set(currency)
        this.$dataSource().data = []
        this.changeDetectorRef.detectChanges()
        this.$dataSource().data = this.priceGraphBuilder.getNodesForDisplay()
        this.updatePrice()
    }

    nodeSelected(node: PricingNode): boolean {
        return this.$selectedTreeNode() === node
    }
}
